import { gql } from "@apollo/client"
import { convertSubunitIntoUnit } from "@oysterhr/common"
import { concatPhrase } from "../../hooks/useKBAdvisor/helpers"
import { RestrictiveCovenantsRules } from "../../hooks/useKBIntegration/fragments"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type {
  CountryIntelligenceRulesRestrictiveCovenants,
  Maybe,
  Money,
  PostTerminationPaymentAmount,
} from "@oysterhr/schema"
import type { AdvisorKnowledgeBase } from "../../types"
import type { RestrictiveCovenantsFormValues } from "~/shared/components/FormSections/RestrictiveCovenantsSection"
import { getSingleRelevantPaymentRule } from "~/shared/components/FormSections/RestrictiveCovenantsSection/helpers/parseRules"

export type Module = AdvisorKnowledgeBase<
  {
    isoAlpha2Code: string
    restrictiveCovenants?: CountryIntelligenceRulesRestrictiveCovenants
  },
  {
    restrictiveCovenantsValues?: RestrictiveCovenantsFormValues
  }
>

export const queryFragment: Module["queryFragment"] = gql`
  fragment RestrictiveCovenantsIntelligencePaymentTypeQuery on CountryIntelligence {
    isoAlpha2Code
    restrictiveCovenants {
      ...RestrictiveCovenantsRules
    }
  }
  ${RestrictiveCovenantsRules}
`

export const TranslationComponent: Module["TranslationComponent"] = ({ data, input }) => {
  const { restrictiveCovenants } = data
  const { restrictiveCovenantsValues } = input?.extraInputs || {}
  const { t } = useTranslation("knowledge_base/HiringAdvisor")

  const phrases = useMemo(() => {
    if (!restrictiveCovenants) return []

    const relevantRule = getSingleRelevantPaymentRule({
      knowledgeBase: restrictiveCovenants,
      currentValues: restrictiveCovenantsValues,
    })

    const statutory = relevantRule?.amountStatutory
    const topOfMarket = relevantRule?.amountTopOfMarket
    const recommended = relevantRule?.amountRecommended

    const getFormattedRule = (rule?: Maybe<PostTerminationPaymentAmount>) => {
      if (rule?.flat?.currencyCode) {
        return { amountValue: formatFlatAmount(rule.flat) }
      } else if (rule?.percentage) {
        return { amountValue: `${rule.percentage}%` }
      }
    }
    const concatAmountRules = (
      key: string,
      value: Maybe<PostTerminationPaymentAmount> | undefined,
    ) => {
      const type: "flat" | "percentage" = value?.flat ? "flat" : "percentage"
      const transKey = `restrictiveCovenants.paymentType.${key}.${type}`
      return concatPhrase(t, content, transKey, getFormattedRule(value))
    }

    let content: string[] = []
    content = concatAmountRules("statutory", statutory)
    content = concatAmountRules("topOfMarket", topOfMarket)
    content = concatAmountRules("recommended", recommended)

    return content || []
  }, [restrictiveCovenants, t, restrictiveCovenantsValues])

  if (!phrases?.length) {
    throw new Error("No payment type rules or unsupported country")
  }

  return (
    <>
      {t("restrictiveCovenants.paymentType.base")}
      <ul className="list-inside list-disc py-2">
        {phrases?.map((phrase, index) => (
          <li key={index} className="pl-4">
            {phrase}
          </li>
        ))}
      </ul>
    </>
  )
}

export const shouldSkipFetch: Module["shouldSkipFetch"] = ({ countryCode, gqlParams }) => {
  return !gqlParams || !countryCode
}

/**
 * Helper function that formats a subunit into a human readable unit
 * @example 300000 cents -> 3000 EUR
 */
export const formatFlatAmount = (
  value: Pick<Money, "subunit" | "currencyCode">,
  options?: Intl.NumberFormatOptions,
) => {
  return `${convertSubunitIntoUnit(value.subunit, value.currencyCode).toLocaleString(
    undefined,
    options,
  )} ${value.currencyCode}`
}
